import Vue from 'vue';
import { reviewRequestService } from '@/services/ReviewRequestService';
import { EventBus } from '@/main';
import { getTriggerConfig, InsightTriggerConfig } from '@/config/insightTriggers';

const STORAGE_PREFIX = 'insight_review';
const FEATURE_UNLOCK_CROSS_CHECK_KEY_PREFIX = 'feature_unlock_last_ask';
const COOLDOWN_DAYS = 30;
const MAX_DISMISSALS = 3;
const MAX_ASKS_PER_YEAR = 6;
const CROSS_CHECK_DAYS = 14;

interface AppStoreLike {
  currentPackage?: {
    price?: number | string | null;
  };
}

class InsightReviewService {
  private triggeredThisSession: Set<string> = new Set();

  public async handleTrigger(storeId: number, triggerType: string, metadata?: any): Promise<void> {
    if (this.triggeredThisSession.has(triggerType)) { return; }

    // Centralized free-plan gate: paid merchants have their own review paths
    // (outreach list, PO triggers) — insight triggers target free users only.
    if (!this.isFreePlan()) { return; }

    const config = getTriggerConfig(triggerType);
    if (!config) { return; }

    if (this.hasTriggeredLifetime(storeId, config)) { return; }
    if (this.isInCooldown(storeId)) { return; }
    if (this.getDismissalCount(storeId) >= MAX_DISMISSALS) { return; }
    if (this.getAskCountThisYear(storeId) >= MAX_ASKS_PER_YEAR) { return; }
    if (this.featureUnlockAskedRecently(storeId)) { return; }

    try {
      const eligibility = await reviewRequestService.checkEligibility('insight');
      if (!eligibility.eligible) { return; }

      // Session dedup set now so concurrent triggers in the same tick are blocked,
      // but lifetime/cooldown state is written only after the dialog is emitted
      // so a failure before emit doesn't permanently lock out this merchant.
      this.triggeredThisSession.add(triggerType);

      if (config.delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, config.delayMs));
      }

      EventBus.$emit('show-review-request-dialog', {
        storeId,
        triggerEvent: triggerType,
        contextMessage: config.contextMessage,
        metadata,
      });

      this.markTriggeredLifetime(storeId, config);
      this.recordAsk(storeId);
      this.setCooldownTimestamp(storeId);
    } catch (error) {
      // Eligibility check failed — silently skip this trigger
    }
  }

  public recordOutcome(
    storeId: number,
    outcome: 'dismissed' | 'link_opened',
  ): void {
    if (outcome === 'dismissed') {
      this.incrementDismissalCount(storeId);
    }
  }

  private isFreePlan(): boolean {
    // $appStore is set on Vue.prototype by App.vue during mount. Services
    // access it through there so they don't need to import the Vuex store.
    // If unavailable (shouldn't happen post-mount), default to NOT firing.
    const appStore = (Vue.prototype as unknown as { $appStore?: AppStoreLike }).$appStore;
    const rawPrice = appStore?.currentPackage?.price;
    if (rawPrice === undefined || rawPrice === null) { return false; }
    const price = typeof rawPrice === 'number' ? rawPrice : Number(rawPrice);
    return Number.isFinite(price) && price === 0;
  }

  // Called by PostUnlockReviewService when a feature-unlock review ask fires,
  // so insight triggers can skip for the cross-check window.
  public recordFeatureUnlockAsk(storeId: number): void {
    try {
      localStorage.setItem(
        `${FEATURE_UNLOCK_CROSS_CHECK_KEY_PREFIX}:${storeId}`,
        Date.now().toString(),
      );
    } catch {
      // localStorage unavailable — non-critical
    }
  }

  private storageKey(storeId: number, suffix: string): string {
    return `${STORAGE_PREFIX}:${storeId}:${suffix}`;
  }

  private hasTriggeredLifetime(storeId: number, config: InsightTriggerConfig): boolean {
    return localStorage.getItem(this.storageKey(storeId, config.localStorageKey)) === 'true';
  }

  private markTriggeredLifetime(storeId: number, config: InsightTriggerConfig): void {
    try {
      localStorage.setItem(this.storageKey(storeId, config.localStorageKey), 'true');
    } catch {
      // localStorage unavailable — non-critical
    }
  }

  private isInCooldown(storeId: number): boolean {
    const lastAsk = localStorage.getItem(this.storageKey(storeId, 'last_ask'));
    if (!lastAsk) { return false; }
    const daysSince = (Date.now() - parseInt(lastAsk, 10)) / (1000 * 60 * 60 * 24);
    return daysSince < COOLDOWN_DAYS;
  }

  private setCooldownTimestamp(storeId: number): void {
    try {
      localStorage.setItem(this.storageKey(storeId, 'last_ask'), Date.now().toString());
    } catch {
      // localStorage unavailable — non-critical
    }
  }

  private getDismissalCount(storeId: number): number {
    return parseInt(localStorage.getItem(this.storageKey(storeId, 'dismissals')) || '0', 10);
  }

  private incrementDismissalCount(storeId: number): void {
    try {
      const count = this.getDismissalCount(storeId) + 1;
      localStorage.setItem(this.storageKey(storeId, 'dismissals'), count.toString());
    } catch {
      // localStorage unavailable — non-critical
    }
  }

  private getAskCountThisYear(storeId: number): number {
    try {
      const raw = localStorage.getItem(this.storageKey(storeId, 'asks'));
      const data: number[] = raw ? JSON.parse(raw) : [];
      const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
      return data.filter((ts) => ts >= yearStart).length;
    } catch {
      return 0;
    }
  }

  private recordAsk(storeId: number): void {
    try {
      const key = this.storageKey(storeId, 'asks');
      const raw = localStorage.getItem(key);
      const existing: number[] = raw ? JSON.parse(raw) : [];
      // Only current-year entries are ever read (getAskCountThisYear), so
      // prune older entries to keep the array bounded across years.
      const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
      const data = existing.filter((ts) => ts >= yearStart);
      data.push(Date.now());
      localStorage.setItem(key, JSON.stringify(data));
    } catch {
      // localStorage unavailable — non-critical
    }
  }

  private featureUnlockAskedRecently(storeId: number): boolean {
    const lastFeatureAsk = localStorage.getItem(`${FEATURE_UNLOCK_CROSS_CHECK_KEY_PREFIX}:${storeId}`);
    if (!lastFeatureAsk) { return false; }
    const daysSince = (Date.now() - parseInt(lastFeatureAsk, 10)) / (1000 * 60 * 60 * 24);
    return daysSince < CROSS_CHECK_DAYS;
  }
}

export const insightReviewService = new InsightReviewService();
